/* eslint-disable no-param-reassign */

import Vuex from 'vuex';
import {createApp} from 'vue';
import {orderBy} from 'lodash';
import { w3cwebsocket as WebSocketClient } from 'websocket';
import * as keys from './keys';
import authenticateApi from '../api/auth';
import caseApi from '../api/case';
import categoriesApi from '../api/categories';
import searchApi from '../api/search';
import { loadContextTheme, clearContextTheme } from '../helpers/load-theme';

const Vue = createApp({});
Vue.use(Vuex);

const searchObservationInterval = 10000;
const getDefaultStoreState = () => ({
	config: {},
	sessionData: {},
	siteConfig: {
		siteTitle: 'GDPR',
		siteSubtitle: 'Avoid getting fined!'
	},
    theme: 'default',
	contextStatus: 'none',
	searchCriteria: {},
    searchId: '',
	activeWebsocket: null,
    caseId: '',
	currentSearch: {
		status: 'new',
		progress: {
			total: 0,
			complete: 0
		}
	},
    currentSearchObservation: {
		status: 'new',
		progress: {
			complete: 0
		}
	},
	resultSelection: [],
	normalizedResults: [],
	currentSearchErrorStatus: {}
});

const store = new Vuex.Store({
	strict: process.env.NODE_ENV === 'development',
	state: getDefaultStoreState(),
	mutations: {
		[keys.SET_SESSION_DATA]: (state, sessionData) => {
			state.sessionData = sessionData;
		},
		[keys.SET_ACTIVE_WEBSOCKET]: (state, websocket) => {
			state.activeWebsocket = websocket;
		},
        [keys.SET_ACTIVE_SEARCH_ID]: (state, searchId) => {
            state.searchId = searchId;
        },
		[keys.SET_CATEGORIES]: (state, categories) => {
			state.categories = categories;
		},
		[keys.SET_THEME]: (state, theme) => {
			state.theme = theme;
		},
		[keys.RESET_STORE_STATE]: (state) => {
			_.assign(state, getDefaultStoreState());
		},
		[keys.SET_SEARCH_CRITERIA]: (state, criteria) => {
			state.searchCriteria = criteria;
		},
		[keys.SET_CASEID]: (state, caseid) => {
			state.caseId = caseid;
		},
        [keys.UPDATE_CURRENT_SEARCH]: (state, currentSearch) => {
            state.currentSearch = currentSearch;
        },
		[keys.SET_SEARCH_RESULTS]: (state, results) => {
			state.normalizedResults = results;
		},
		[keys.UPDATE_OBSERVATION]: (state) => {
            state.currentSearchObservation.status = state.currentSearch.status;
            state.currentSearchObservation.progress.complete = state.currentSearch.progress.complete;
		},
		[keys.FAIL_AS_INACTIVE]: (state) => {
            if (['fresh', 'running'].includes(state.currentSearch.status)) {
                state.currentSearch.status = 'error';
                state.activeWebsocket.close();
            }
        },
		[keys.SET_CURRENT_SEARCH_ERROR_STATUS]: (state, status) => {
			state.currentSearchErrorStatus = status;
		}
	},
	actions: {
        [keys.CREATE_CASEID]: ({ commit }) => {
			return caseApi.create().then(response => {
				commit(keys.SET_CASEID, response.caseId);
			});
		},
    [keys.SEARCH_CASEID]: ({ commit }, caseId) => {
        return caseApi.get(caseId).then(response => {
          commit(keys.SET_CASEID, response.caseId);
        });
    },
		[keys.LOAD_CATEGORIES]: ({ commit }) => {
			return categoriesApi.get().then(response => {
				commit(keys.SET_CATEGORIES, orderBy(response, ['label']));
			});
		},
    [keys.FIND_SEARCH_BY_CASEID]: (obj) => {
      return searchApi.getByCaseId(obj.getters.CASEID)
      .catch(err => {
        if (typeof err === 'string' && err.match(/Not Found/)) {
          return 'no-search';
        }
        throw err;
      }).then((resp) => {
        return resp;
      });
    },
		[keys.OBSERVE_SEARCH]: ({commit, state, dispatch}) => {
            const hasChanged = (current, last) => {
                return (current.status !== last.status)
                    || (current.progress.complete !== last.progress.complete);
            };

			if (['fresh', 'running'].includes(state.currentSearch.status)) {
				if (hasChanged(state.currentSearch, state.currentSearchObservation)) {
					commit(keys.UPDATE_OBSERVATION);
					setTimeout(() => { dispatch(keys.OBSERVE_SEARCH); }, searchObservationInterval);
				} else {
					searchApi.isRunning(state.searchId).then(response => {
						if (response.running === true) {
							setTimeout(() => { dispatch(keys.OBSERVE_SEARCH); }, searchObservationInterval);
						} else {
							commit(keys.FAIL_AS_INACTIVE);
						}
					});
				}
			}
        },
        [keys.LOAD_SEARCH_PROGRESS]: ({commit, state, dispatch}) => {
            return new Promise((resolve) => {
                const client = new WebSocketClient(`${process.env.WS_URL}/search/${state.searchId}/progress`);
                client.onerror = () => { console.log('Connection error'); };
                client.onmessage = (message) => {
                    const jsonMessage = JSON.parse(message.data);
                    commit(keys.UPDATE_CURRENT_SEARCH, jsonMessage);
                    if (['done', 'limit-reached'].includes(jsonMessage.status)) {
                        client.close();
                    }
                };
                commit(keys.SET_ACTIVE_WEBSOCKET, client);

                setTimeout(() => {
                    dispatch(keys.OBSERVE_SEARCH);
				}, searchObservationInterval);

                resolve(client);
            });
        },
		[keys.PERFORM_SEARCH]: ({ commit, state }, criteria) => {
			return searchApi.create({
				caseId: state.caseId,
				criteria
			}).then((response) => {
				commit(keys.SET_SEARCH_CRITERIA, criteria);
				commit(keys.SET_ACTIVE_SEARCH_ID, response.searchId);
			});
		},
		[keys.CANCEL_SEARCH]: ({ commit, state }) => {
			return searchApi.cancel(state.searchId).then(() => {
				if (state.activeWebsocket) {
					state.activeWebsocket.close();
					commit(keys.SET_ACTIVE_WEBSOCKET, null);
				}
				commit(keys.SET_SEARCH_CRITERIA, {});
				commit(keys.SET_ACTIVE_SEARCH_ID, '');
			});
		},
		[keys.LOAD_SEARCH_RESULTS]: ({ commit, state }) => {
			return searchApi.get(state.searchId).then((results) => {
				const inc = (() => {
					let i = 0;
					return () => {
						const r = i;
						i += 1;
						return r;
					};
				})();
            const normalizedResults = _.flatMap(results.results, (result) => {
            return _.map(result.results, (row) => {
						return {
							id: inc(),
							rowData: row,
							location: result.location
						};
					});
				});
            commit(keys.SET_CURRENT_SEARCH_ERROR_STATUS, {
				errorMsg: results.errorMsg,
				errorCount: results.errorCount,
                totalLocations: results.totalLocations
			});
            commit(keys.SET_SEARCH_RESULTS, normalizedResults);
			});
		},
        // [keys.LOAD_PRESET_LIST]: ({ commit }) => {
		// 	return presetsApi.list().then(presets => {
		// 		commit(keys.SET_PRESET_LIST, presets);
		// 	});
		// },
		[keys.LOAD_SESSION_DATA]: ({ commit }) => {
			return authenticateApi.getSessionData().then(sessionData => commit(keys.SET_SESSION_DATA, sessionData));
		},
		[keys.LOAD_THEME]: ({ state, commit }) => {
			clearContextTheme();
			return loadContextTheme(_.get(state.config, ['ui', 'theme'])).then(theme => commit(keys.SET_THEME, theme));
		},
		[keys.LOGOUT]: ({ commit }) => {
			authenticateApi.logout();
			commit(keys.RESET_STORE_STATE);
			clearContextTheme();
		}
	},
	getters: {
		[keys.USER_DISPLAY_NAME]: state => {
			return _.get(state.sessionData, ['userdata', 'displayName']);
		},
		[keys.CONFIGURATION]: state => state.config,
		[keys.SITE_TITLE]: state => state.siteConfig.siteTitle,
		[keys.SITE_SUBTITLE]: state => state.siteConfig.siteSubtitle,
		[keys.THEME]: state => state.theme,
		[keys.SEARCH_CRITERIA]: state => state.searchCriteria,
		[keys.CASEID]: state => state.caseId,
		[keys.CATEGORIES]: state => state.categories,
        [keys.ACTIVE_SEARCH_ID]: state => state.searchId,
        [keys.CURRENT_SEARCH]: state => state.currentSearch,
		[keys.SEARCH_RESULTS]: state => state.normalizedResults,
        [keys.SEARCH_RESULTS_TABLE]: state => {
            const tableResults = [];
            const searchResults = state.normalizedResults;
            searchResults.forEach((result) => {
                const tableRow = {
                    id: result.id,
                    system: result.location.sourceSystem,
					database: result.location.database,
                    table: result.location.table,
                    rowData: result.rowData,
					location: result.location
                };

                const categories = _.groupBy(result.location.targetColumns, 'category');
                const criteria = state.searchCriteria;
                _.each(Object.keys(criteria), (key) => {
                    const searchData = _.map(categories[key], (c) => result.rowData[c.name]);
                    _.each(searchData, (datum) => {
                        if (datum && datum.toUpperCase().includes(criteria[key].toUpperCase())) {
                            tableRow[key] = datum;
                        }
                    });
                });
                tableResults.push(tableRow);
            });
            return tableResults;
        },
        [keys.SEARCH_RESULTS_COLUMNS]: state => {
            const columns = [
                {
                    label: 'System',
                    field: 'system',
					name: 'system'
                },
                {
                    label: 'Database',
                    field: 'database',
					name: 'database'
                },
                {
                    label: 'Table',
                    field: 'table',
					name: 'table'
                }
            ];
            _.each(Object.keys(state.searchCriteria), (key) => {
                columns.push({
                    label: key,
                    field: key,
					name: key
                });
            });
            columns.push({
                label: 'Details',
                field: 'details',
				name: 'details'
            });
            return columns;
        },
        [keys.CURRENT_SEARCH_ERROR_STATUS]: state => state.currentSearchErrorStatus
	}
});

export default store;
